/**
 * newsletter-form.js
 * ────────────────────────────────────────────────────────────────────────
 * Sendet Newsletter-Anmeldungen direkt zu Klaviyo (statt Shopify-Customer)
 * via Klaviyo Client Subscriptions API (Public-Key, CORS-enabled).
 *
 * Mapping: form-Selector → { source, listId }
 *   #footer-newsletter                → Gewinnspiel-Liste
 *   .blog-index__newsletter-form      → Education-Liste
 *
 * Double-Opt-In wird über Klaviyo's List-Settings gesteuert (empfohlen ON).
 */

(function () {
  'use strict';

  const KLAVIYO_COMPANY_ID = 'VKQMJQ';

  /* Selector-basiertes Mapping (Liquid {% form %}-Tag erlaubt keine
     data-attributes mit Hyphen als Argument-Namen, daher per Selector). */
  const FORM_REGISTRY = [
    {
      selector: '#footer-newsletter',
      source: 'footer',
      listId: 'SgH4b8',
      sourceLabel: 'Footer Gewinnspiel',
    },
    {
      selector: '.blog-index__newsletter-form',
      source: 'blog',
      listId: 'WvKKCx',
      sourceLabel: 'Blog Newsletter',
    },
    {
      selector: '.article__mid-newsletter-form',
      source: 'article',
      listId: 'WvKKCx',
      sourceLabel: 'Article Mid-Newsletter',
    },
  ];

  FORM_REGISTRY.forEach((entry) => {
    document.querySelectorAll(entry.selector).forEach((form) => init(form, entry));
  });

  /* MutationObserver falls Forms dynamisch nach DOM-load eingefügt werden */
  const obs = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((n) => {
        if (n.nodeType !== 1) return;
        FORM_REGISTRY.forEach((entry) => {
          if (n.matches?.(entry.selector)) init(n, entry);
          n.querySelectorAll?.(entry.selector).forEach((f) => init(f, entry));
        });
      });
    }
  });
  obs.observe(document.body, { childList: true, subtree: true });

  function init(form, entry) {
    if (form._newsletterFormInit) return;
    form._newsletterFormInit = true;
    form.addEventListener('submit', (e) => handleSubmit(e, form, entry));
  }

  async function handleSubmit(e, form, entry) {
    e.preventDefault();
    const { source, listId, sourceLabel } = entry;

    const emailInput = form.querySelector('input[type="email"]');
    const firstNameInput = form.querySelector('input[name="contact[first_name]"]');
    const submitBtn = form.querySelector('button[type="submit"]');

    const email = (emailInput?.value || '').trim();
    const firstName = (firstNameInput?.value || '').trim();

    if (!email || !email.includes('@')) {
      showError(form, 'Bitte gib eine gültige E-Mail-Adresse ein.');
      emailInput?.focus();
      return;
    }

    /* Loading-State (Webflow-Original: data-wait="Bitte warten...") */
    const originalText = submitBtn?.textContent.trim();
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.textContent = 'Bitte warten...';
    }
    clearError(form);

    /* Klaviyo-Payload.
       Sprint 015ah Bug-Fix (2026-06-09): Vorheriger Code hatte `subscriptions`
       Field im profile.attributes. Klaviyo Client-API revision 2024-10-15
       akzeptiert das NICHT mehr: "'subscriptions' is not a valid field for
       the resource 'profile'." (400 invalid). Marketing-Consent ist implizit
       gegeben durch das Subscriben an die Liste. Field komplett entfernt. */
    const profileAttrs = { email };
    if (firstName) profileAttrs.first_name = firstName;

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: sourceLabel || source,
          profile: { data: { type: 'profile', attributes: profileAttrs } },
        },
        relationships: {
          list: { data: { type: 'list', id: listId } },
        },
      },
    };

    try {
      const res = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-10-15',
          },
          body: JSON.stringify(payload),
        }
      );

      if (res.ok || res.status === 202) {
        showSuccess(form, source);
      } else {
        const text = await res.text().catch(() => '');
        console.error('[newsletter-form] Klaviyo error', res.status, text);
        throw new Error(`HTTP ${res.status}`);
      }
    } catch (err) {
      console.error('[newsletter-form] Subscription failed:', err);
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-busy');
        submitBtn.textContent = originalText || 'Anmelden';
      }
      showError(form, 'Ups, es ist ein Fehler aufgetreten. Bitte versuche es später nocheinmal.');
    }
  }

  function showSuccess(form, source) {
    /* Webflow-Original Success-Text. */
    const messages = {
      footer: 'Vielen Dank für die Blumen.',
      blog: 'Vielen Dank für die Blumen.',
      article: 'Vielen Dank für die Blumen.',
    };
    const msg = form.dataset.successMessage || messages[source] ||
      'Vielen Dank für die Blumen.';
    const wrap = document.createElement('div');
    wrap.className = 'newsletter-form__success';
    wrap.setAttribute('role', 'status');
    wrap.textContent = msg;
    form.replaceWith(wrap);
  }

  function showError(form, message) {
    let errEl = form.querySelector('[data-newsletter-error]');
    if (!errEl) {
      errEl = document.createElement('p');
      errEl.setAttribute('data-newsletter-error', '');
      errEl.className = 'newsletter-form__error';
      errEl.setAttribute('role', 'alert');
      form.appendChild(errEl);
    }
    errEl.textContent = message;
  }

  function clearError(form) {
    const errEl = form.querySelector('[data-newsletter-error]');
    if (errEl) errEl.remove();
  }
})();
